/**
 * Where saved games and settings live. Phase 1 uses the browser (localStorage).
 * Phase 2 can add a cloud version of this same interface without touching the UI.
 */
import type { GameState } from '../game/state.ts';
import type { SolveRecord, StartRecord } from '../game/stats.ts';
import type { Level, PublicPuzzle } from '../game/types.ts';

export type StyleName = 'minimal' | 'playful';
export type ModeName = 'system' | 'light' | 'dark';

/** What the first tap of Notes in a game does. */
export type NotesStart = 'ask' | 'fill' | 'blank';

export interface Settings {
  style: StyleName;
  mode: ModeName;
  showTimer: boolean;
  notesStart: NotesStart;
}

export const DEFAULT_SETTINGS: Settings = { style: 'minimal', mode: 'system', showTimer: true, notesStart: 'ask' };

/** The parts of a game worth keeping between visits. */
export interface SavedGame {
  version: 2;
  puzzle: PublicPuzzle;
  values: string;
  notesKind: GameState['notesKind'];
  notes: number[];
  struck: number[];
  undo: GameState['undo'];
  redo: GameState['redo'];
  notesMode: boolean;
  wrong: number[];
  checksUsed: number;
  hintsUsed: number;
  seenHints: string[];
  autoNotesUsed: boolean;
  elapsedMs: number;
  startedAt: string;
  savedAt: string;
}

export interface GameStorage {
  loadGame(level: Level): SavedGame | null;
  saveGame(game: GameState): void;
  clearGame(level: Level): void;
  loadSettings(): Settings;
  saveSettings(settings: Settings): void;
  /** IDs of recently played puzzles, newest last, so new games avoid repeats. */
  recentPuzzles(level: Level): string[];
  rememberPuzzle(level: Level, id: string): void;
  /** Stats: a game's board was shown for the first time. */
  recordStart(start: StartRecord): void;
  /** Stats: a game was solved. Recording the same game twice has no effect. */
  recordSolve(solve: SolveRecord): void;
  loadStats(): { solves: SolveRecord[]; starts: StartRecord[] };
  /** Deletes all stats. Saved games in progress are not touched. */
  resetStats(): void;
  /** Whether How to play has been shown automatically on this device. */
  introSeen(): boolean;
  markIntroSeen(): void;
}

export function toSaved(g: GameState, now = new Date()): SavedGame {
  return {
    version: 2,
    puzzle: g.puzzle,
    values: g.values.join(''),
    notesKind: g.notesKind,
    notes: g.notes,
    struck: g.struck,
    undo: g.undo,
    redo: g.redo,
    notesMode: g.notesMode,
    wrong: g.wrong,
    checksUsed: g.checksUsed,
    hintsUsed: g.hintsUsed,
    seenHints: g.seenHints,
    autoNotesUsed: g.autoNotesUsed,
    elapsedMs: g.elapsedMs,
    startedAt: g.startedAt,
    savedAt: now.toISOString(),
  };
}

export function fromSaved(saved: SavedGame): GameState {
  const given = Array.from(saved.puzzle.givens, (ch) => ch !== '0');
  const values = Array.from(saved.values, Number);
  return {
    puzzle: saved.puzzle,
    given,
    values,
    notesKind: saved.notesKind,
    notes: saved.notes,
    struck: saved.struck,
    undo: saved.undo,
    redo: saved.redo,
    selected: values.findIndex((v) => v === 0),
    notesMode: saved.notesMode,
    wrong: saved.wrong,
    checksUsed: saved.checksUsed,
    hintsUsed: saved.hintsUsed,
    seenHints: saved.seenHints.filter((k) => typeof k === 'string'),
    autoNotesUsed: saved.autoNotesUsed === true,
    elapsedMs: saved.elapsedMs,
    status: 'playing',
    pauseReason: null,
    hint: null,
    startedAt: saved.startedAt,
  };
}

/** Rejects anything that isn't a well-formed saved game (old versions, edits, corruption). */
export function isSavedGame(x: unknown, level: Level): x is SavedGame {
  const s = x as SavedGame;
  return (
    !!s &&
    s.version === 2 &&
    s.puzzle?.level === level &&
    ['none', 'manual', 'auto'].includes(s.notesKind) &&
    Array.isArray(s.struck) &&
    s.struck.length === 81 &&
    Array.isArray(s.seenHints) &&
    /^[0-9]{81}$/.test(s.puzzle.givens) &&
    /^[0-9]{81}$/.test(s.values) &&
    Array.isArray(s.notes) &&
    s.notes.length === 81 &&
    Array.isArray(s.undo) &&
    Array.isArray(s.redo) &&
    Number.isFinite(s.elapsedMs)
  );
}

const RECENT_LIMIT = 30;
/** Enough history for years of play while staying well inside browser storage limits. */
const STATS_LIMIT = 5000;
/**
 * Saved games and recent-puzzle lists live under a versioned prefix; when their
 * shape or meaning changes the version goes up and older ones are removed (agreed,
 * not migrated). v2: five levels. v3: new notes (fill or blank, strike-offs).
 * Settings and stats keep their own fixed keys, so they survive these changes.
 */
const DATA_VERSION = 3;
const key = (name: string) =>
  name === 'settings'
    ? 'sudoku:v1:settings'
    : name.startsWith('stats:') || name === 'seen-intro'
      ? `sudoku:v2:${name}`
      : `sudoku:v${DATA_VERSION}:${name}`;

function removeOldVersions(store: Pick<Storage, 'removeItem'> & { key?: (i: number) => string | null; length?: number }) {
  try {
    if (typeof store.key !== 'function' || typeof store.length !== 'number') return;
    const old: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      const m = k?.match(/^sudoku:v(\d+):(game|recent):/);
      if (k && m && Number(m[1]) < DATA_VERSION) old.push(k);
    }
    old.forEach((k) => store.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** localStorage version. Every read and write tolerates storage being unavailable. */
export function createLocalStorage(
  store: (Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & Partial<Pick<Storage, 'key' | 'length'>>) | null,
): GameStorage {
  if (store) removeOldVersions(store);
  const read = <T>(name: string): T | null => {
    try {
      const raw = store?.getItem(key(name));
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  };
  const write = (name: string, value: unknown) => {
    try {
      store?.setItem(key(name), JSON.stringify(value));
    } catch {
      /* storage full or blocked: the game still works, it just won't be saved */
    }
  };

  return {
    loadGame(level) {
      const g = read<unknown>(`game:${level}`);
      return isSavedGame(g, level) ? g : null;
    },
    saveGame(game) {
      if (game.status === 'won') return;
      write(`game:${game.puzzle.level}`, toSaved(game));
    },
    clearGame(level) {
      try {
        store?.removeItem(key(`game:${level}`));
      } catch {
        /* ignore */
      }
    },
    loadSettings() {
      const s = read<Partial<Settings>>('settings') ?? {};
      return {
        style: s.style === 'playful' ? 'playful' : 'minimal',
        mode: s.mode === 'light' || s.mode === 'dark' ? s.mode : 'system',
        showTimer: s.showTimer !== false,
        notesStart: s.notesStart === 'fill' || s.notesStart === 'blank' ? s.notesStart : 'ask',
      };
    },
    saveSettings(settings) {
      write('settings', settings);
    },
    recentPuzzles(level) {
      const ids = read<unknown>(`recent:${level}`);
      return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
    },
    rememberPuzzle(level, id) {
      const ids = this.recentPuzzles(level).filter((x) => x !== id);
      ids.push(id);
      write(`recent:${level}`, ids.slice(-RECENT_LIMIT));
    },
    recordStart(start) {
      const starts = this.loadStats().starts;
      if (starts.some((s) => s.puzzleId === start.puzzleId && s.startedAt === start.startedAt)) return;
      write('stats:starts', [...starts, start].slice(-STATS_LIMIT));
    },
    recordSolve(solve) {
      const solves = this.loadStats().solves;
      if (solves.some((s) => s.puzzleId === solve.puzzleId && s.startedAt === solve.startedAt)) return;
      write('stats:solves', [...solves, solve].slice(-STATS_LIMIT));
    },
    loadStats() {
      const list = <T>(name: string, ok: (x: T) => boolean): T[] => {
        const raw = read<unknown>(name);
        return Array.isArray(raw) ? (raw as T[]).filter((x) => !!x && typeof x === 'object' && ok(x)) : [];
      };
      return {
        starts: list<StartRecord>('stats:starts', (s) => typeof s.puzzleId === 'string' && typeof s.level === 'string'),
        solves: list<SolveRecord>(
          'stats:solves',
          (s) => typeof s.puzzleId === 'string' && typeof s.level === 'string' && Number.isFinite(s.elapsedMs),
        ),
      };
    },
    introSeen() {
      return read<boolean>('seen-intro') === true;
    },
    markIntroSeen() {
      write('seen-intro', true);
    },
    resetStats() {
      try {
        store?.removeItem(key('stats:starts'));
        store?.removeItem(key('stats:solves'));
      } catch {
        /* ignore */
      }
    },
  };
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export const storage: GameStorage = createLocalStorage(browserStorage());
